import { toggleQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { textQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { equipmentQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { timestampQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";

import {
  SensorTopologyDetails,
  TopologyCorrection,
} from "./SensorTopologyDetails";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  useGetSensorTopologyQuery,
  useListRecordingPositionsQuery,
} from "src/store/api/generatedApi";
import { SensorTopologyGraph } from "./SensorTopologyGraph";
import { SensorTopologyTimeline } from "./SensorTopologyTimeline";
import { SensorTopologyChangeDialog } from "./SensorTopologyChangeDialog";
import { ConnectionEditor } from "./SensorConnectionsSection";
import { InstallationEditor } from "./SensorInstallationsSection";
import { TopologyEdit } from "./topologyEditing";
import {
  formatInstallationTimestamp,
  installationInstant,
} from "./installationTimestamp";

export const SensorTopologyPanel = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const [at, setAt] = useNavigationQuery(
    "installationAt",
    () => dayjs().toISOString(),
    timestampQuery,
    "replace"
  );
  const [selection, setSelection] = useNavigationQuery(
    "equipment",
    null,
    equipmentQuery,
    "push"
  );
  const [deviceOrder, setDeviceOrder] = useNavigationQuery(
    "deviceOrder",
    "relationships",
    choiceQuery(["relationships", "alphabetical"] as const),
    "replace"
  );
  const [search, setSearch] = useNavigationQuery(
    "equipmentSearch",
    "",
    textQuery,
    "replace"
  );
  const [showInactive, setShowInactive] = useNavigationQuery(
    "inactiveEquipment",
    false,
    toggleQuery,
    "replace"
  );
  const [edit, setEdit] = useState<TopologyEdit | null>(null);
  const [correction, setCorrection] = useState<TopologyCorrection | null>(null);
  const {
    data: topology,
    isFetching,
    error,
    refetch,
  } = useGetSensorTopologyQuery({ tenantName, at });
  const { data: positions, error: positionsError } =
    useListRecordingPositionsQuery({ tenantName });
  const busy =
    isFetching ||
    topology == null ||
    installationInstant(topology.at) !== installationInstant(at);
  const closeEditor = () => {
    setEdit(null);
    setCorrection(null);
    void refetch();
  };
  if (topology == null)
    return error != null ? (
      <Alert severity="error">
        {fetchErrorDetail(error) ?? t("sensorHistoryLoadError")}
        <Button onClick={() => void refetch()}>{t("refresh")}</Button>
      </Alert>
    ) : (
      <LinearProgress />
    );
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          label={t("topologySearch")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label={t("sortedBy")}
          value={deviceOrder}
          sx={{ minWidth: 210 }}
          onChange={(event) => {
            if (
              event.target.value === "relationships" ||
              event.target.value === "alphabetical"
            )
              setDeviceOrder(event.target.value);
          }}
        >
          <MenuItem value="relationships">{t("topologyRelatedOrder")}</MenuItem>
          <MenuItem value="alphabetical">
            {t("topologyAlphabeticalOrder")}
          </MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(_, checked) => {
                setShowInactive(checked);
              }}
            />
          }
          label={t("topologyShowInactive")}
        />
        <Button
          disabled={busy}
          onClick={() => {
            setShowInactive(true);
            setEdit({ kind: "device" });
          }}
        >
          {t("topologyAddDevice")}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            setShowInactive(true);
            setEdit({ kind: "sensor" });
          }}
        >
          {t("topologyAddSensor")}
        </Button>
        <Button onClick={() => void refetch()}>{t("refresh")}</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {t("topologyDragHint")}
      </Typography>
      {(error != null || positionsError != null) && (
        <Alert severity="error">
          {fetchErrorDetail(error ?? positionsError) ??
            t("sensorHistoryLoadError")}
        </Alert>
      )}
      {isFetching && <LinearProgress />}
      <Typography variant="subtitle2">
        {t("topologyStateAt", {
          time: formatInstallationTimestamp(topology.at),
        })}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" },
          gap: 2,
        }}
      >
        <SensorTopologyGraph
          topology={topology}
          deviceOrder={deviceOrder}
          positions={positions?.items ?? []}
          selection={selection}
          onSelect={setSelection}
          onEdit={setEdit}
          search={search}
          showInactive={showInactive}
          busy={busy}
        />
        <SensorTopologyDetails
          key={selection == null ? "none" : `${selection.kind}-${selection.id}`}
          topology={topology}
          positions={positions?.items ?? []}
          selection={selection}
          busy={busy}
          onEdit={setEdit}
          onCorrect={setCorrection}
          onClear={() => {
            setSelection(null);
          }}
        />
      </Box>
      <SensorTopologyTimeline
        topology={topology}
        sensorId={selection?.kind === "sensor" ? selection.id : null}
        at={at}
        onChange={setAt}
      />
      {edit != null && (
        <SensorTopologyChangeDialog
          edit={edit}
          topology={topology}
          positions={positions?.items ?? []}
          onClose={closeEditor}
        />
      )}
      {correction?.kind === "connection" && (
        <ConnectionEditor
          connection={correction.connection}
          onClose={closeEditor}
        />
      )}
      {correction?.kind === "installation" && (
        <InstallationEditor
          installation={correction.installation}
          onClose={closeEditor}
        />
      )}
    </Stack>
  );
};
